import React, { useState, useCallback } from 'react';
import './LicenseDialog.css';

const LicenseDialog = ({ isOpen, onClose, onLicenseValidated }) => {
  const [licenseToken, setLicenseToken] = useState('');
  const [email, setEmail] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState('');
  const [requiresEmail, setRequiresEmail] = useState(false);

  const handleValidate = useCallback(async () => {
    if (!licenseToken.trim()) {
      setError('Please enter a license token');
      return;
    }

    // Check if this is a partner token
    const isPartnerToken = licenseToken.trim().startsWith('PIXL-AMBASSADOR-');
    
    // Partner tokens require email
    if (isPartnerToken && !email.trim()) {
      setError('Please enter your email address for PIXL Ambassador license activation');
      setRequiresEmail(true);
      return;
    }

    setIsValidating(true);
    setError('');

    try {
      // Check if we're in Electron or web browser
      if (!window.electronAPI) {
        setError('This feature requires the desktop application. Please download PixlSizer.');
        return;
      }
      
      // Pass email along with token for partner licenses
      const validationData = {
        token: licenseToken.trim(),
        ...(isPartnerToken && { email: email.trim() })
      };
      
      const result = await window.electronAPI.validateLicense(validationData);
      
      if (result.success) {
        // Pass all the data including token to parent
        const licenseData = {
          token: licenseToken.trim(),
          tier: result.data.tier,
          features: result.data.features,
          email: result.data.email || email.trim(),
          expiresAt: result.data.expiresAt
        };
        
        onLicenseValidated(licenseData);
        onClose();
      } else {
        setError(result.error || 'Invalid license token');
        if (result.requiresEmail) {
          setRequiresEmail(true);
        }
      }
    } catch (error) {
      console.error('License validation error:', error);
      setError('Failed to validate license. Please check your internet connection.');
    } finally {
      setIsValidating(false);
    }
  }, [licenseToken, email, onLicenseValidated, onClose]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !isValidating) {
      handleValidate();
    }
  }, [handleValidate, isValidating]);

  if (!isOpen) return null;

  return (
    <div className="license-dialog-overlay">
      <div className="license-dialog glass-card">
        <div className="license-dialog-header">
          <h2>Activate PixlSizer</h2>
          <p>Enter your license token to activate the application</p>
        </div>
        
        <div className="license-dialog-content">
          <div className="license-input-group">
            <label htmlFor="license-token">License Token</label>
            <input
              id="license-token"
              type="text"
              className="glass-input license-token-input"
              value={licenseToken}
              onChange={(e) => {
                setLicenseToken(e.target.value);
                // Check if it's a partner token to show email field
                if (e.target.value.startsWith('PIXL-AMBASSADOR-')) {
                  setRequiresEmail(true);
                } else {
                  setRequiresEmail(false);
                }
              }}
              onKeyPress={handleKeyPress}
              placeholder="Paste your PixlSizer token here"
              disabled={isValidating}
              autoFocus
            />
          </div>
          
          {requiresEmail && (
            <div className="license-input-group">
              <label htmlFor="email">Email Address</label>
              <input
                id="email"
                type="email"
                className="glass-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="your@email.com"
                disabled={isValidating}
                autoComplete="email"
              />
              <p className="help-text" style={{ marginTop: '8px' }}>
                PIXL Ambassador licenses require the email address associated with your account
              </p>
            </div>
          )}
          
          {error && (
            <div className="license-error">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}
          
          <div className="license-help">
            <p>
              <strong>Don't have a license?</strong> 
              <a 
                href="#" 
                onClick={(e) => {
                  e.preventDefault();
                  if (window.electronAPI) {
                    window.electronAPI.openExternal('https://pixlsizer.com');
                  } else {
                    window.open('https://pixlsizer.com', '_blank');
                  }
                }}
                className="purchase-link"
              >
                Purchase PixlSizer
              </a>
            </p>
            <p className="help-text">
              Your license token was sent to your email after purchase. 
              Check your spam folder if you can't find it.
            </p>
          </div>
        </div>
        
        <div className="license-dialog-actions">
          <button
            className="glass-button glass-button-secondary"
            onClick={onClose}
            disabled={isValidating}
          >
            Cancel
          </button>
          <button
            className="glass-button"
            onClick={handleValidate}
            disabled={!licenseToken.trim() || isValidating}
          >
            {isValidating ? (
              <>
                <span className="spinner">⟳</span>
                Validating...
              </>
            ) : (
              'Activate License'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LicenseDialog;